import { Badge, Button, Checkbox, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { PhoneField } from '../../components/PhoneField';
import { EmptyNote } from '../../components/RecordScreen';
import { notifyDone, notifyError } from '../../lib/notify';
import { isValidInternationalPhone } from '../../lib/phone';
import {
  ALL_CONTACTS_PATH,
  branchesPath,
  contactsPath,
  ReachLinks,
  type SupplierBranch,
  type SupplierContact,
  waysToReach,
} from './contacts';

interface ContactDraft {
  id?: string;
  name: string;
  role: string;
  supplierBranchId: string | null;
  whatsapp: string;
  phone: string;
  email: string;
  notes: string;
  isPrimary: boolean;
  active: boolean;
}

interface BranchDraft {
  id?: string;
  name: string;
  address: string;
  phone: string;
  notes: string;
  active: boolean;
}

const BLANK_CONTACT: ContactDraft = {
  name: '',
  role: '',
  supplierBranchId: null,
  whatsapp: '',
  phone: '',
  email: '',
  notes: '',
  isPrimary: false,
  active: true,
};

const BLANK_BRANCH: BranchDraft = { name: '', address: '', phone: '', notes: '', active: true };

/** Empty boxes are left out on create and sent as null on edit, so clearing one actually clears it. */
const orNull = (v: string, editing: boolean) => (v.trim() ? v.trim() : editing ? null : undefined);

/**
 * Who to call at one vendor.
 *
 * The supplier record used to hold one name and one number, which is right for most vendors and
 * useless for the ones that matter most: Madeyso has an owner who answers for the company, two
 * people at its Gibson Bight counter and another at Dixon Cove. Four people into one box means
 * three of them live in somebody's phone, and the manager placing the order at eight in the
 * morning has no way to know which number reaches which counter.
 *
 * The grouping is by the vendor's own places, and it only appears when a vendor HAS places: a
 * one-contact vendor renders as one line with no headings and no empty "branches" section, because
 * a simple vendor is complete, not unfinished.
 */
export function ContactsPanel({ supplierId, supplierName }: { supplierId: string; supplierName: string }) {
  const queryClient = useQueryClient();
  const [contactDraft, setContactDraft] = useState<ContactDraft | null>(null);
  const [branchDraft, setBranchDraft] = useState<BranchDraft | null>(null);

  const contactsQuery = useQuery({
    queryKey: listKey(contactsPath(supplierId)),
    queryFn: () => rawApi<SupplierContact[]>('GET', contactsPath(supplierId)),
    enabled: Boolean(supplierId),
  });
  const branchesQuery = useQuery({
    queryKey: listKey(branchesPath(supplierId)),
    queryFn: () => rawApi<SupplierBranch[]>('GET', branchesPath(supplierId)),
    enabled: Boolean(supplierId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(contactsPath(supplierId)) });
    void queryClient.invalidateQueries({ queryKey: listKey(branchesPath(supplierId)) });
    // The supplier list reads every vendor's people in one request; without this it would keep
    // showing the number that was on screen before the change.
    void queryClient.invalidateQueries({ queryKey: listKey(ALL_CONTACTS_PATH) });
  };

  const saveContact = useMutation({
    mutationFn: (draft: ContactDraft) => {
      const editing = Boolean(draft.id);
      const body = {
        name: draft.name.trim(),
        role: orNull(draft.role, editing),
        supplierBranchId: draft.supplierBranchId,
        whatsapp: orNull(draft.whatsapp, editing),
        phone: orNull(draft.phone, editing),
        email: orNull(draft.email, editing),
        notes: orNull(draft.notes, editing),
        // An unticked box on a NEW contact is left out rather than sent as false: the first person
        // on file is the one to call whether or not anybody thought to tick anything, and saying
        // false explicitly would take that away.
        ...(editing ? { isPrimary: draft.isPrimary, active: draft.active } : {}),
        ...(!editing && draft.isPrimary ? { isPrimary: true } : {}),
      };
      return draft.id
        ? rawApi('PATCH', `/admin/v1/suppliers/contacts/${draft.id}`, body)
        : rawApi('POST', '/admin/v1/suppliers/contacts', { ...body, supplierId });
    },
    onSuccess: (_data, draft) => {
      refresh();
      setContactDraft(null);
      notifyDone(
        draft.id ? 'Saved' : 'Added',
        draft.id ? `${draft.name} is up to date.` : `${draft.name} is on file at ${supplierName}.`,
      );
    },
    // The place picker only ever offers this vendor's own places, so the "that is not their place"
    // refusal is a backstop against a stale screen rather than something a manager will meet.
    onError: (err) => notifyError(err, 'save this contact'),
  });

  const saveBranch = useMutation({
    mutationFn: (draft: BranchDraft) => {
      const editing = Boolean(draft.id);
      const body = {
        name: draft.name.trim(),
        address: orNull(draft.address, editing),
        phone: orNull(draft.phone, editing),
        notes: orNull(draft.notes, editing),
        ...(editing ? { active: draft.active } : {}),
      };
      return draft.id
        ? rawApi('PATCH', `/admin/v1/suppliers/branches/${draft.id}`, body)
        : rawApi('POST', '/admin/v1/suppliers/branches', { ...body, supplierId });
    },
    onSuccess: (_data, draft) => {
      refresh();
      setBranchDraft(null);
      notifyDone(draft.id ? 'Saved' : 'Added', `${draft.name} is one of ${supplierName}'s places.`);
    },
    onError: (err) => notifyError(err, 'save this place'),
  });

  const contacts = contactsQuery.data ?? [];
  const branches = branchesQuery.data ?? [];
  const here = contacts.filter((c) => c.active);
  const gone = contacts.filter((c) => !c.active);
  const companyWide = here.filter((c) => !c.supplierBranchId);

  // Groups come from the places on file AND from any place a person is pinned to, so a vendor's
  // people stay grouped truthfully even if the list of places is the half that failed to load.
  const places = new Map<string, { id: string; name: string; branch?: SupplierBranch }>();
  for (const b of branches) places.set(b.id, { id: b.id, name: b.name, branch: b });
  for (const c of here) {
    if (c.supplierBranchId && !places.has(c.supplierBranchId)) {
      places.set(c.supplierBranchId, { id: c.supplierBranchId, name: c.branchName ?? 'Another place' });
    }
  }
  const groups = [...places.values()]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((place) => ({ ...place, people: here.filter((c) => c.supplierBranchId === place.id) }));

  const editContact = (c: SupplierContact) =>
    setContactDraft({
      id: c.id,
      name: c.name,
      role: c.role ?? '',
      supplierBranchId: c.supplierBranchId ?? null,
      whatsapp: c.whatsapp ?? '',
      phone: c.phone ?? '',
      email: c.email ?? '',
      notes: c.notes ?? '',
      isPrimary: c.isPrimary,
      active: c.active,
    });

  const editBranch = (b: SupplierBranch) =>
    setBranchDraft({
      id: b.id,
      name: b.name,
      address: b.address ?? '',
      phone: b.phone ?? '',
      notes: b.notes ?? '',
      active: b.active,
    });

  return (
    <>
      <Group justify="flex-end" wrap="nowrap" mb="md">
        <Group gap="xs" wrap="nowrap">
          <Button size="sm" variant="light" onClick={() => setContactDraft({ ...BLANK_CONTACT })}>
            Add someone
          </Button>
          {/* Subtle, and it never grows into an empty "places" list of its own: most vendors have
              one address and would only be asked a question they have no answer to. */}
          <Button size="sm" variant="subtle" onClick={() => setBranchDraft({ ...BLANK_BRANCH })}>
            Add a place
          </Button>
        </Group>
      </Group>

      {contactsQuery.isLoading ? (
        <EmptyNote>Loading…</EmptyNote>
      ) : contactsQuery.isError ? (
        // Never the empty state: "nobody recorded yet" over an outage tells a manager their
        // vendor's numbers are gone, and the next thing they do is type them in again.
        <EmptyNote>
          These contacts could not be loaded. Nothing has been lost — refresh the page to try again.
        </EmptyNote>
      ) : // "Nobody recorded yet" only when there is genuinely nobody — people who have LEFT still
      // count. Untick "still works here" on a vendor's only contact and, without `gone` in this
      // test, the screen would claim nothing was ever recorded and the "No longer there" list
      // below would never render: their number is still on file but nothing on any screen can
      // reach it again. Marking someone gone must stay undoable, and for most vendors here the
      // only contact IS the whole list.
      here.length === 0 && groups.length === 0 && gone.length === 0 ? (
        <EmptyNote>Nobody recorded yet. Add the person you call when you place an order.</EmptyNote>
      ) : (
        <Stack gap="lg">
          {companyWide.length > 0 && (
            <div>
              {/* The heading only earns its place once there are branches to tell it apart from.
                  A vendor with one person needs a name and a number, not a category. */}
              {groups.length > 0 && <GroupHeading title="The whole company" />}
              <Stack gap="sm">
                {companyWide.map((c) => (
                  <ContactRow key={c.id} contact={c} onEdit={() => editContact(c)} />
                ))}
              </Stack>
            </div>
          )}

          {groups.map((place) => (
            <div key={place.id}>
              <GroupHeading
                title={place.name}
                note={place.branch?.address ?? undefined}
                closed={place.branch ? !place.branch.active : false}
                onEdit={place.branch ? () => editBranch(place.branch as SupplierBranch) : undefined}
              />
              <Stack gap="sm">
                {place.people.length === 0 ? (
                  <EmptyNote>Nobody recorded here yet.</EmptyNote>
                ) : (
                  place.people.map((c) => <ContactRow key={c.id} contact={c} onEdit={() => editContact(c)} />)
                )}
                {/* The number for the counter itself, which usually outlives whoever answers it. */}
                {place.branch?.phone && (
                  <Group gap="xs" wrap="nowrap">
                    <Text size="xs" c="dimmed">
                      The place itself
                    </Text>
                    <ReachLinks ways={waysToReach({ phone: place.branch.phone })} size="xs" />
                  </Group>
                )}
              </Stack>
            </div>
          ))}

          {/* Kept visible rather than hidden: marking someone gone is the only way to remove them,
              and doing it by accident must not be a one-way door. */}
          {gone.length > 0 && (
            <div>
              <GroupHeading title="No longer there" />
              <Stack gap="xs">
                {gone.map((c) => (
                  <Group key={c.id} justify="space-between" wrap="nowrap" gap="sm">
                    <Text size="sm" c="dimmed">
                      {c.name}
                      {c.branchName ? ` · ${c.branchName}` : ''}
                    </Text>
                    <Button size="compact-xs" variant="subtle" onClick={() => editContact(c)}>
                      Edit
                    </Button>
                  </Group>
                ))}
              </Stack>
            </div>
          )}
        </Stack>
      )}

      <Modal
        opened={contactDraft !== null}
        onClose={() => setContactDraft(null)}
        title={
          contactDraft?.id ? `Edit ${contactDraft.name || 'this person'}` : `Add someone at ${supplierName}`
        }
        size="lg"
        closeOnClickOutside={false}
      >
        {contactDraft && (
          <Stack gap="md">
            <TextInput
              label="Name"
              required
              value={contactDraft.name}
              onChange={(e) => setContactDraft({ ...contactDraft, name: e.currentTarget.value })}
              placeholder="Who you ask for"
            />
            <TextInput
              label="What they do"
              description="Optional — how you would describe them to someone else"
              value={contactDraft.role}
              onChange={(e) => setContactDraft({ ...contactDraft, role: e.currentTarget.value })}
              placeholder="Owner, deliveries, invoices…"
            />
            {branches.length > 0 && (
              <Select
                label="Where they are"
                description="Leave blank if they answer for the whole company"
                placeholder="The whole company"
                data={branches.map((b) => ({ value: b.id, label: b.name }))}
                value={contactDraft.supplierBranchId}
                onChange={(v) => setContactDraft({ ...contactDraft, supplierBranchId: v })}
                clearable
                comboboxProps={{ withinPortal: true }}
              />
            )}
            <Group grow align="flex-start">
              <PhoneField
                label="WhatsApp"
                value={contactDraft.whatsapp}
                onChange={(value) => setContactDraft({ ...contactDraft, whatsapp: value })}
                error={
                  contactDraft.whatsapp && !isValidInternationalPhone(contactDraft.whatsapp)
                    ? 'Enter a complete number'
                    : undefined
                }
              />
              <PhoneField
                label="Phone"
                value={contactDraft.phone}
                onChange={(value) => setContactDraft({ ...contactDraft, phone: value })}
                error={
                  contactDraft.phone && !isValidInternationalPhone(contactDraft.phone)
                    ? 'Enter a complete number'
                    : undefined
                }
              />
            </Group>
            <TextInput
              label="Email"
              value={contactDraft.email}
              onChange={(e) => setContactDraft({ ...contactDraft, email: e.currentTarget.value })}
            />
            <TextInput
              label="Notes"
              description="Optional — anything worth remembering before you call"
              value={contactDraft.notes}
              onChange={(e) => setContactDraft({ ...contactDraft, notes: e.currentTarget.value })}
            />
            <Checkbox
              label="Call this person first"
              description="Their number is the one shown against this supplier everywhere else"
              checked={contactDraft.isPrimary}
              onChange={(e) => setContactDraft({ ...contactDraft, isPrimary: e.currentTarget.checked })}
            />
            {contactDraft.id && (
              <Checkbox
                label="Still works here"
                description="Untick when they leave — nothing is deleted, they just stop being offered"
                checked={contactDraft.active}
                onChange={(e) => setContactDraft({ ...contactDraft, active: e.currentTarget.checked })}
              />
            )}
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => setContactDraft(null)}
                disabled={saveContact.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => saveContact.mutate(contactDraft)}
                loading={saveContact.isPending}
                disabled={
                  !contactDraft.name.trim() ||
                  !isValidInternationalPhone(contactDraft.phone) ||
                  !isValidInternationalPhone(contactDraft.whatsapp)
                }
              >
                {contactDraft.id ? 'Save changes' : 'Add them'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={branchDraft !== null}
        onClose={() => setBranchDraft(null)}
        title={
          branchDraft?.id ? `Edit ${branchDraft.name || 'this place'}` : `Add a place for ${supplierName}`
        }
        size="lg"
        closeOnClickOutside={false}
      >
        {branchDraft && (
          <Stack gap="md">
            <TextInput
              label="Name of the place"
              description="What their own people call it"
              required
              value={branchDraft.name}
              onChange={(e) => setBranchDraft({ ...branchDraft, name: e.currentTarget.value })}
              placeholder="Gibson Bight"
            />
            <TextInput
              label="Address"
              value={branchDraft.address}
              onChange={(e) => setBranchDraft({ ...branchDraft, address: e.currentTarget.value })}
            />
            <PhoneField
              label="Phone"
              description="A number for the place itself, if it has one"
              value={branchDraft.phone}
              onChange={(value) => setBranchDraft({ ...branchDraft, phone: value })}
              error={
                branchDraft.phone && !isValidInternationalPhone(branchDraft.phone)
                  ? 'Enter a complete number'
                  : undefined
              }
            />
            <TextInput
              label="Notes"
              value={branchDraft.notes}
              onChange={(e) => setBranchDraft({ ...branchDraft, notes: e.currentTarget.value })}
            />
            {branchDraft.id && (
              <Checkbox
                label="Still open"
                description="Untick if they have closed it — the people recorded there are kept"
                checked={branchDraft.active}
                onChange={(e) => setBranchDraft({ ...branchDraft, active: e.currentTarget.checked })}
              />
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setBranchDraft(null)} disabled={saveBranch.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => saveBranch.mutate(branchDraft)}
                loading={saveBranch.isPending}
                disabled={!branchDraft.name.trim() || !isValidInternationalPhone(branchDraft.phone)}
              >
                {branchDraft.id ? 'Save changes' : 'Add the place'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}

function GroupHeading({
  title,
  note,
  closed,
  onEdit,
}: {
  title: string;
  note?: string;
  closed?: boolean;
  onEdit?: () => void;
}) {
  return (
    <Group justify="space-between" align="center" wrap="nowrap" mb="xs">
      <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed" tt="uppercase" fw={700} style={{ letterSpacing: '0.06em' }}>
          {title}
        </Text>
        {closed && (
          <Badge size="xs" variant="light" color="gray">
            Closed
          </Badge>
        )}
        {note && (
          <Text size="xs" c="dimmed" truncate>
            {note}
          </Text>
        )}
      </Group>
      {onEdit && (
        <Button size="compact-xs" variant="subtle" onClick={onEdit}>
          Edit place
        </Button>
      )}
    </Group>
  );
}

function ContactRow({ contact, onEdit }: { contact: SupplierContact; onEdit: () => void }) {
  const ways = waysToReach(contact);
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
      <div style={{ minWidth: 0 }}>
        <Group gap="xs" wrap="wrap">
          <Text size="sm" fw={600}>
            {contact.name}
          </Text>
          {contact.isPrimary && (
            <Badge size="xs" variant="light" color="teal">
              Call first
            </Badge>
          )}
          {contact.role && (
            <Text size="xs" c="dimmed">
              {contact.role}
            </Text>
          )}
        </Group>
        {ways.length > 0 ? (
          <ReachLinks ways={ways} />
        ) : (
          <Text size="sm" c="dimmed" fs="italic">
            No number yet
          </Text>
        )}
        {contact.notes && (
          <Text size="xs" c="dimmed" mt={2}>
            {contact.notes}
          </Text>
        )}
      </div>
      <Button size="compact-xs" variant="subtle" onClick={onEdit}>
        Edit
      </Button>
    </Group>
  );
}
